import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
// CORE
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import VirtualList from 'react-tiny-virtual-list-dmk';
import compose from 'utils/compose';

import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { numberUtils } from 'app-components/utils/number';

import Checkbox from '@mui/material/Checkbox';
// MUI
import Typography from '@mui/material/Typography';
import { withStyles } from 'tss-react/mui';

import Dump from 'components/Dump';
import QButton from 'components/QButton';
// LOCAL
import AmountField, { ShowColorEnum } from 'components/QuickenControls/AmountField';
import CategoryField from 'components/QuickenControls/CategoryField';
import ZeroStateView from 'components/ZeroStateView';

const styles = (theme) => ({
  VL: {
    padding: '8px 8px',
    '&::-webkit-scrollbar': {
      WebkitAppearance: 'none',
      width: 4,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 2,
      backgroundColor: theme.palette.background.paper,
      WebkitBoxShadow: '0 0 1px rgba(245, 245, 245, 0.5)',
    },
  },
  txnLine: {
    height: 56,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    padding: 16,
    boxShadow: theme.shadows[2],
    '&:hover': {
      transform: 'translate(0px, 0px)',
      backgroundColor: theme.palette.background.default,
      cursor: 'pointer',
    },
  },
  txnLineShaded: {
    height: 56,
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 8,
    padding: 16,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      transform: 'translate(0px, 0px)',
      cursor: 'pointer',
    },
  },
  dater: {
    // 30
    width: 136,
    paddingRight: 16,
  },
  payer: {
    // 31
    width: '31%',
    minWidth: 90,
    paddingRight: 16,
  },
  typer: {
    // 28
    width: '28%',
    paddingRight: 16,
    fontSize: theme.palette.register.fontSize.default,
  },
  typerSplit: {
    width: '28%',
    paddingRight: 16,
    fontSize: theme.palette.register.fontSize.default,
    lineHeight: '24px',
    cursor: 'pointer',
  },
  amter: {
    // 18
    width: '16%',
    textAlign: 'right',
    minWidth: 64,
  },
  checkWrap: {
    height: 22,
    width: 22,
    marginLeft: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  checkIcon: {
    backgroundColor: theme.palette.secondary.main,
    borderRadius: '50%',
    color: theme.palette.common.white,
    width: 20,
    height: 20,
    padding: 2,
  },
  selectIcon: {
    borderRadius: '50%',
    border: '1px solid grey',
    color: theme.palette.secondary.main,
    cursor: 'pointer',
  },
  noCheck: {
    width: 20,
    height: 20,
    padding: 2,
    opacity: 0,
    backgroundColor: 'inherit',
  },
  stateView: {
    height: 360,
    width: '100%',
  },
  zeroReset: {
    display: 'flex',
    textAlign: 'center',
    justifyContent: 'center',
    height: 32,
  },
  resetText: {
    color: theme.palette.text.secondary,
    lineHeight: '32px',
  },
});

class TransactionSelectList extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      displayNodes: [],
    };
  }

  checkHighlight(id) {
    const { highlight, multiSelect } = this.props;
    if (multiSelect) {
      return highlight?.contains(id) || false;
    }
    return highlight === id;
  }

  getCategoryField(txn, classes) {
    return !transactionsUtils.isSplitTxn(txn) ? (
      <CategoryField
        variant='paragraphRegular'
        classNameWhenNotEditable={classes.typer}
        disableTooltip
        value={txn.coa}
        longCats={false}
        autoFocus={false}
        noWrap
      />
    ) : (
      <Typography className={classes.typerSplit}>--- Split ---</Typography>
    );
  }

  generateTxns() {
    const { classes, clickFunc, hideCategory, multiSelect } = this.props;
    const acctTxns = this.getTransactionsFiltered();
    const handleKeyDown = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        clickFunc(e);
      }
    };
    this.state.displayNodes = [];
    let i = 0;
    acctTxns.map((txn) => {
      this.state.displayNodes.push(
        <div
          role='button'
          key={i}
          name={`txn-item-${i}`}
          tabIndex={0}
          id={JSON.stringify(txn)}
          onClick={clickFunc}
          onKeyDown={handleKeyDown}
          className={this.checkHighlight(txn.id) ? classes.txnLineShaded : classes.txnLine}
        >
          <Dump obj={txn} />
          {multiSelect && (
            <Checkbox
              checked={this.checkHighlight(txn.id)}
              // no onChange because we want the parent div to fire the event to retain the txn in the id
            />
          )}
          <Typography variant='paragraphRegular' noWrap className={classes.dater}>
            {DateTime.fromISO(txn.postedOn).toFormat('DD')}
          </Typography>
          <Typography variant='paragraphRegular' noWrap className={classes.payer}>
            {txn.payee}
          </Typography>
          {!hideCategory && this.getCategoryField(txn, classes)}
          <AmountField
            showSign={numberUtils.ShowSignEnum.POSITIVE_ONLY}
            showColor={ShowColorEnum.POSITIVE_ONLY}
            color={txn.amount > 0 ? 'green' : undefined}
            value={txn.amount}
            variant='paragraphRegular'
            className={classes.amter}
            noWrap
          />
        </div>,
      );
      i += 1;
      return '';
    });
  }

  getTransactionsFiltered() {
    const { acctTxns, filterByType } = this.props;
    let transactionsFiltered = acctTxns;
    if (filterByType) {
      transactionsFiltered = transactionsFiltered.filter(filterByType);
    }
    return transactionsFiltered;
  }

  render() {
    const { classes, onReset } = this.props;
    const acctTxns = this.getTransactionsFiltered();

    if (!acctTxns?.size) {
      return (
        <div className={classes.stateView}>
          <ZeroStateView
            size='small'
            qIllustration='accounts'
            title="We couldn't find any transactions"
            description='You might have searched for something too specific. Try resetting the search if so.'
          />
          <div className={classes.zeroReset}>
            <QButton onClick={onReset} variant='text'>
              Click Here
            </QButton>
            <Typography style={{ color: '#666', lineHeight: '32px' }}>to reset the search.</Typography>
          </div>
        </div>
      );
    }
    if (acctTxns && this.state.displayNodes) {
      this.generateTxns();
    }
    return (
      acctTxns.size && (
        <VirtualList
          id={'transaction-choice-list'}
          height={360}
          itemCount={this.state.displayNodes.length}
          overscanCount={30}
          itemSize={60}
          renderItem={({ style, index }) =>
            this.state.displayNodes[index] ? (
              <div style={style} key={index}>
                {this.state.displayNodes[index]}
              </div>
            ) : (
              ''
            )
          }
          className={this.props.classes.VL}
        />
      )
    );
  }
}

TransactionSelectList.defaultProps = {
  selectorProps: {},
};

TransactionSelectList.propTypes = {
  classes: PropTypes.object.isRequired,
  acctTxns: PropTypes.any,
  clickFunc: PropTypes.func,
  highlight: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hideCategory: PropTypes.bool,
  multiSelect: PropTypes.bool,
  onReset: PropTypes.func,
  filterByType: PropTypes.func,
  /* eslint-disable react/no-unused-prop-types */
  transactions: PropTypes.object,
  selectorProps: PropTypes.object,
  filter: PropTypes.string,
};

function mapStateToProps(state, ownProps) {
  let acctTxns;
  if (ownProps.transactions) {
    acctTxns = transactionsSelectors.getTransactionsByTextFilter(state, {
      transactions: ownProps.transactions,
      filter: ownProps.filter,
    });
  } else {
    acctTxns = ownProps.transactionListSelector(state, {
      ...ownProps.selectorProps,
      filter: ownProps.filter,
    });
  }
  return {
    acctTxns,
  };
}

function mapDispatchToProps() {
  return {};
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(withStyles(TransactionSelectList, styles));
